import React, { useState, useRef, useEffect, useMemo } from "react";
import _ from "lodash";

// Import custom components and utilities
import { Icon } from "../styledComponents";
import { InTableFilterList } from "./InTableFilterList";
import { DynamicRenderContent } from "./DynamicContentRender";
import { FilterList } from "./FilterList";
import { ElementContainer } from "../../containers/styledComponents";
import { Filter as Option } from "../../../types/PageFilterTypes";
import { lightGreyText } from "../../../styles";
import { DEFAULT_FILTER } from "../../../utils/PageFilterUtils";
import { MESSAGES } from "../../../messages";
import downArrow from "../../../assets/svg/down_arrow_icon.svg";
import { CompassParagraph } from "../../common/styledComponents";
import {
  AnimatedIconContainer,
  DropdownList,
  Filter,
  FilterContainer,
} from "./styledComponents";

// Placeholder constants for when no default option is selected
const NO_DEFAULT_OPTION_PLACEHOLDER_KEY = "NO_DEFAULT_OPTION";
const NO_DEFAULT_OPTION_PLACEHOLDER: Option = {
  key: NO_DEFAULT_OPTION_PLACEHOLDER_KEY,
  displayValue: NO_DEFAULT_OPTION_PLACEHOLDER_KEY,
};

export const DATE_OPTION_KEY = "DATE_OPTION_KEY";

// CustomDropdown component
export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  onSelectOption,
  onSelectOptions,
  isMultiSelect = false,
  isInTable = false,
  placeholder = NO_DEFAULT_OPTION_PLACEHOLDER,
  isLoading = false,
  override,
  small = false,
  animate = true,
}) => {
  // References
  const dropdownRef = useRef<HTMLDivElement>(null);

  // State variables
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<Option>(
    placeholder.key === NO_DEFAULT_OPTION_PLACEHOLDER_KEY
      ? options[0] || DEFAULT_FILTER
      : placeholder
  );
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  // The Date option is a special case where the component not be displayed, nor should the dropdown close on selection
  const isDatePicker = options[0]?.key === DATE_OPTION_KEY;
  const shouldDisplayComponent = selectedOption.component && !isDatePicker;
  const shouldCloseOnSelect = !isMultiSelect && !isDatePicker;

  // Combine placeholder and options to create the list of options to display
  const allSelectableOptions: Option[] = useMemo(() => {
    if (options[0]?.selectorName) {
      placeholder.selectorName = options[0].selectorName;
    }
    return placeholder.key === NO_DEFAULT_OPTION_PLACEHOLDER_KEY
      ? options
      : [DEFAULT_FILTER, ...options];
  }, [options, placeholder.key]);

  // Toggle the dropdown open or closed
  const toggleDropdown = (): void => {
    if (!isLoading) {
      setIsOpen((prev) => !prev);
    }
  };

  // Handle external override changes
  useEffect(() => {
    if (override && override.key && override.key !== selectedOption.key) {
      const selectedOverride = allSelectableOptions.find(
        (option) => option.key === override.key
      );

      if (selectedOverride) {
        handleSelectOption(selectedOverride);
      }
    }
  }, [override]);

  // Update selected options when options prop changes (for multi-select)
  useEffect(() => {
    if (isMultiSelect) {
      const updatedSelectedOptions = selectedOptions.filter((option) =>
        allSelectableOptions.some((opt) => opt.key === option.key)
      );
      setSelectedOptions(updatedSelectedOptions);
      if (updatedSelectedOptions.length === 0) setSelectedOption(placeholder);
    }

    // If the Date option is selected, make sure to select the first option which now contains the date
    if (isDatePicker && selectedOption.key === DATE_OPTION_KEY) {
      setSelectedOption(options[0]);
    }
  }, [allSelectableOptions]);

  // Effect to handle clicks outside the dropdown and trigger onSelectOptions when closing
  useEffect(() => {
    // Function to handle clicks outside the dropdown
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Add event listener when dropdown is open
      window.addEventListener("click", handleClickOutside);
    }

    // When dropdown is closed and there are changes, call onSelectOptions
    if (!isOpen && hasChanges && onSelectOptions) {
      onSelectOptions(selectedOptions, selectedOption.selectorName);
      setHasChanges(false);
    }

    // Cleanup: remove event listener
    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen]);

  // Helper function to check if an option exists in the selected options
  const doesOptionExist = (
    options: Option[],
    targetOption: Option
  ): boolean => {
    return options.some((opt) => _.isEqual(opt, targetOption));
  };

  // Handle selecting an option
  const handleSelectOption = (option: Option): void => {
    const validSelectedOption =
      option.displayValue === MESSAGES.NO_FILTER_APPLIED ? placeholder : option;

    setSelectedOption(validSelectedOption);

    if (onSelectOption && !isMultiSelect) {
      onSelectOption(validSelectedOption);
    }

    if (isMultiSelect) {
      handleMultiSelect(option);
    }

    if (shouldCloseOnSelect) {
      setIsOpen(false); // Close dropdown after selection
    }
  };

  // Handle selecting/deselecting options in multi-select mode
  const handleMultiSelect = (option: Option): void => {
    setHasChanges(true);
    let updatedSelectedOptions: Option[] = [];

    if (doesOptionExist(selectedOptions, option)) {
      // If option is already selected, remove it
      updatedSelectedOptions = selectedOptions.filter(
        (item) => item.key !== option.key
      );
    } else {
      // Add the selected option
      if (option.displayValue !== MESSAGES.NO_FILTER_APPLIED) {
        updatedSelectedOptions = [...selectedOptions, option];
      } else {
        updatedSelectedOptions = [];
      }
    }

    setSelectedOptions(updatedSelectedOptions);

    if (updatedSelectedOptions.length === 0) {
      setSelectedOption(placeholder);
    }
  };

  // Prevent the dropdown from closing when clicking inside it
  const handleDropdownClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
  };

  // Get the text to display for the selected option(s)
  const getDisplayedValue = (): string => {
    if (!isMultiSelect || selectedOptions.length === 0)
      return getFormattedDisplayValue(selectedOption.displayValue);

    if (selectedOptions.length === 1)
      return getFormattedDisplayValue(selectedOptions[0].displayValue);

    return `${selectedOptions.length} items selected`;
  };

  // Format the display value, truncating if too long
  const getFormattedDisplayValue = (displayValue: string): string => {
    if (displayValue.length > 40) {
      return displayValue.slice(0, 40) + "...";
    }
    return displayValue;
  };

  return (
    <FilterContainer
      key={options[options.length - 1]?.key}
      className="dropdown-container"
      ref={dropdownRef}
      isInTable={isInTable}
      isLoading={isLoading}
      small={small}
    >
      {/* Container for the filter display */}
      <ElementContainer
        ai="flex-start"
        flexDirection="column"
        jc="center"
        mr={-1}
        ml={-1}
        className="filter-container"
      >
        {/* Filter display and toggle button */}
        <ElementContainer
          ai="center"
          flexDirection="row"
          jc="center"
          mr={-1}
          ml={-1}
          className="filter-item-container"
        >
          <Filter
            onClick={toggleDropdown}
            isDefaultColor={
              selectedOption.displayValue === placeholder.displayValue
            }
            isInTable
            small={small}
          >
            {shouldDisplayComponent ? (
              <DynamicRenderContent
                option={selectedOption}
                animate={isInTable}
              />
            ) : (
              <CompassParagraph
                fontSize={small ? 13 : 14}
                color={lightGreyText}
                mr={-1}
                cursor="pointer"
              >
                {getDisplayedValue()}
              </CompassParagraph>
            )}
          </Filter>
          {/* Dropdown arrow icon */}
          <AnimatedIconContainer
            size={small ? 18 : 25}
            p={1}
            m={1}
            br={50}
            onClick={toggleDropdown}
            isPointingDown={!isOpen}
          >
            <Icon src={downArrow} />
          </AnimatedIconContainer>
        </ElementContainer>
        {/* In-table filter list (for isInTable) */}
        {isOpen &&
          isInTable &&
          allSelectableOptions.map((option, index) => (
            <InTableFilterList
              key={`${index}-in-table-filter-${option.key}`}
              option={option}
              handleSelectOption={handleSelectOption}
            />
          ))}
      </ElementContainer>
      {/* Dropdown list (for not in-table) */}
      {isOpen && !isInTable && (
        <DropdownList
          className="dropdown-list"
          onClick={handleDropdownClick}
          small={small}
        >
          {allSelectableOptions.map((option, index) => (
            <FilterList
              key={`${index}-selection-item-container-${option.key}`}
              option={option}
              handleSelectOption={handleSelectOption}
              isMultiSelect={isMultiSelect}
              checked={doesOptionExist(selectedOptions, option)}
              animate={animate}
            />
          ))}
        </DropdownList>
      )}
    </FilterContainer>
  );
};

// Props interface for the CustomDropdown component
export interface CustomDropdownProps {
  options: Option[];
  isMultiSelect?: boolean;
  onSelectOption?: (option: Option) => void;
  onSelectOptions?: (options: Option[], selectorName?: string) => void;
  isInTable?: boolean;
  placeholder?: Option;
  override?: Option;
  isLoading?: boolean;
  small?: boolean;
  animate?: boolean;
}

// Type definition for the onSelectOptions function
export type onSelectionFuncType = (
  options: Option[],
  selectorName?: string
) => void;
